// =====================================================
// BED — Accès au brouillon serveur du wizard (migration 00177)
// =====================================================
// Toutes les écritures du participant passent par des RPC SECURITY DEFINER
// scopées par `bed_access_token` : la table `bed_drafts` n'a AUCUNE policy anon
// (cf. en-tête de 00177 — `form_submissions` est lisible par toute clé anon, on
// n'y met donc pas de passeports). Aucune de ces fonctions ne throw : elles
// renvoient un statut discriminé, comme le reste du module BED.

import { supabase } from '../supabase';
import type { BedPrefill, BedPrefillSolde } from './prefill';
import type { BedDeferralMap, BedMissingItem, BedStepId, BedStepState } from './wizard-model';

// -----------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------

/** Ligne de la grille tarifaire exposée au participant. */
export interface BedPricingItem {
  pricing_item_id: string;
  label: string;
  amount_cents: number;
  /** Document de conditions (assureur). Non nul → « j'ai pris connaissance » exigé. */
  conditions_url: string | null;
}

/** Signature tracée d'un participant. */
export interface BedSignature {
  /** PNG en data-URL (cf. justification §6 de 00177). */
  data_url: string;
  signed_at: string;
}

/** Certificat médical déposé (bucket privé `bed-uploads`). */
export interface BedCertificate {
  path: string;
  name: string;
  size: number;
  uploaded_at: string;
}

/** Sélection d'options + preuves de lecture, telles que persistées. */
export interface BedDraftOptions {
  selected_item_ids: string[];
  /** `{ [pricing_item_id]: conditions_url affichée au moment de la coche }`. */
  acknowledged: Record<string, string>;
}

export interface BedDraft {
  values: Record<string, string>;
  deferred: BedDeferralMap;
  step_states: Partial<Record<BedStepId, BedStepState>>;
  last_step: number | null;
  signatures: Partial<Record<'pilot' | 'copilot', BedSignature>>;
  options: Partial<BedDraftOptions>;
  medical_certificates: Partial<Record<'pilot' | 'copilot', BedCertificate>>;
  amount_to_pay_cents: number | null;
  missing_items: BedMissingItem[];
  submitted_at: string | null;
  payment_case_id: string | null;
  updated_at: string;
}

export interface BedBootstrap {
  prefill: BedPrefill;
  solde: BedPrefillSolde | null;
  /** Date proposée pour « je m'engage à communiquer avant le … ». */
  info_deadline: string | null;
  mandatory_items: BedPricingItem[];
  optional_items: BedPricingItem[];
  draft: BedDraft | null;
  /**
   * Statut CRM du dossier APRÈS l'ouverture du lien (migration 00178) :
   * `get_bed_bootstrap` pose « BED téléchargé » si le porteur était encore
   * pré-inscrit, puis renvoie l'état d'après. Relayé tel quel dans l'événement
   * postMessage `loaded`.
   */
  bed_status: string | null;
  bed_status_label: string | null;
}

/** Patch d'auto-save : uniquement ce que l'étape courante a modifié. */
export interface BedDraftPatch {
  values?: Record<string, string>;
  deferred?: BedDeferralMap;
  step_states?: Partial<Record<BedStepId, BedStepState>>;
  last_step?: number;
  signatures?: Partial<Record<'pilot' | 'copilot', BedSignature>>;
  options?: BedDraftOptions;
  medical_certificates?: Partial<Record<'pilot' | 'copilot', BedCertificate>>;
  amount_to_pay_cents?: number | null;
}

export type BedTokenError = 'invalid' | 'expired' | 'already_submitted' | 'error';

export type BedBootstrapResult =
  | { status: 'ok'; data: BedBootstrap }
  | { status: BedTokenError; code?: string };

export type BedSaveResult =
  | { status: 'ok'; savedAt: string; missingItems: BedMissingItem[] }
  | { status: BedTokenError; code?: string };

// -----------------------------------------------------------------------
// MAPPING D'ERREURS
// -----------------------------------------------------------------------

/**
 * Les RPC lèvent des erreurs NOMMÉES (`bed_access_token_expired`, …). On les
 * traduit en statut, sans jamais exposer le message brut à l'utilisateur.
 */
function classifyError(message: string | undefined): BedTokenError {
  if (!message) return 'error';
  if (message.includes('_expired')) return 'expired';
  if (message.includes('_invalid')) return 'invalid';
  if (message.includes('already_submitted')) return 'already_submitted';
  return 'error';
}

// -----------------------------------------------------------------------
// APPELS
// -----------------------------------------------------------------------

/**
 * Charge en UN aller-retour tout ce dont le wizard a besoin : pré-remplissage
 * CRM, brouillon serveur du même lien, grille tarifaire (obligatoires +
 * options avec leurs documents), solde SERVEUR et deadline « infos manquantes ».
 */
export async function getBedBootstrap(token: string): Promise<BedBootstrapResult> {
  try {
    const { data, error } = await supabase.rpc('get_bed_bootstrap', { p_token: token });
    if (error) {
      const status = classifyError(error.message);
      if (status === 'error') console.error('[BED-DRAFT] get_bed_bootstrap:', error);
      return { status, code: error.message };
    }
    if (!data) return { status: 'invalid' };
    return { status: 'ok', data: data as BedBootstrap };
  } catch (err) {
    console.error('[BED-DRAFT] Exception get_bed_bootstrap:', err);
    return { status: 'error' };
  }
}

/**
 * Enregistre l'étape courante. Le serveur fusionne le patch avec le brouillon
 * existant et RECALCULE les éléments manquants : c'est sa liste qui fait foi.
 */
export async function saveBedDraft(token: string, patch: BedDraftPatch): Promise<BedSaveResult> {
  try {
    const { data, error } = await supabase.rpc('save_bed_draft', {
      p_token: token,
      p_patch: patch,
    });
    if (error) {
      const status = classifyError(error.message);
      if (status === 'error') console.error('[BED-DRAFT] save_bed_draft:', error);
      return { status, code: error.message };
    }
    const result = data as { saved_at: string; missing_items: BedMissingItem[] };
    return {
      status: 'ok',
      savedAt: result?.saved_at ?? new Date().toISOString(),
      missingItems: result?.missing_items ?? [],
    };
  } catch (err) {
    console.error('[BED-DRAFT] Exception save_bed_draft:', err);
    return { status: 'error' };
  }
}

export type BedSchoolResult =
  | { status: 'ok'; id: string; name: string; city: string; created: boolean }
  | { status: 'missing_city' }
  | { status: BedTokenError; code?: string };

/**
 * Crée (ou retrouve) l'école saisie par le participant. Cahier L46 : nom ET
 * ville obligatoires — la RPC refuse explicitement une création sans ville.
 */
export async function createBedSchool(
  token: string,
  name: string,
  city: string
): Promise<BedSchoolResult> {
  try {
    const { data, error } = await supabase.rpc('bed_create_school', {
      p_token: token,
      p_name: name,
      p_city: city,
    });
    if (error) {
      if (error.message?.includes('school_name_and_city_required')) {
        return { status: 'missing_city' };
      }
      const status = classifyError(error.message);
      if (status === 'error') console.error('[BED-DRAFT] bed_create_school:', error);
      return { status, code: error.message };
    }
    const result = data as { id: string; name: string; city: string; created: boolean };
    return { status: 'ok', ...result };
  } catch (err) {
    console.error('[BED-DRAFT] Exception bed_create_school:', err);
    return { status: 'error' };
  }
}

/** Détail du solde renvoyé par le socle 00166 après finalisation. */
export interface BedBreakdown {
  mandatory_items: Array<BedPricingItem & { acknowledged: boolean }>;
  acompte_cents: number;
  reduction_cents: number;
  reduction_label: string | null;
  solde_base_cents: number | null;
  options_catalog: Array<BedPricingItem & { acknowledged: boolean; selected: boolean }>;
  subscribed_options: Array<{ label: string; amount_cents: number }>;
  required_acks_missing: Array<{ pricing_item_id: string; label: string }>;
  total_cents: number;
  status: string;
  locked: boolean;
  pricing_unverified: boolean;
  detail_stale: boolean;
}

export interface BedFinalizeParams {
  token: string;
  paymentCaseId: string;
  submissionIds: string[];
  optionItemIds: string[];
  /** `{ [pricing_item_id]: conditions_url AFFICHÉE }` — le socle exige l'écho. */
  conditionsUrls: Record<string, string>;
  amountToPayCents: number | null;
}

export type BedFinalizeResult =
  | {
      status: 'ok';
      breakdown: BedBreakdown | null;
      missingItems: BedMissingItem[];
      amountToPayCents: number | null;
      complete: boolean;
      /** Statut CRM après la transition de soumission (00178). */
      bedStatus: string | null;
    }
  | { status: BedTokenError; code?: string };

/**
 * Clôture la soumission : branche la sélection d'options et les prises de
 * connaissance sur le socle 00166 (`set_bed_case_options` /
 * `acknowledge_bed_conditions`), fige le brouillon et y archive la liste
 * structurée des éléments manquants. Aucun calcul financier n'est refait ici.
 */
export async function finalizeBedSubmission(params: BedFinalizeParams): Promise<BedFinalizeResult> {
  try {
    const { data, error } = await supabase.rpc('finalize_bed_submission', {
      p_token: params.token,
      p_payment_case_id: params.paymentCaseId,
      p_submission_ids: params.submissionIds,
      p_option_item_ids: params.optionItemIds,
      p_conditions_urls: params.conditionsUrls,
      p_amount_to_pay_cents: params.amountToPayCents,
    });
    if (error) {
      const status = classifyError(error.message);
      if (status === 'error') console.error('[BED-DRAFT] finalize_bed_submission:', error);
      return { status, code: error.message };
    }
    const result = data as {
      breakdown: BedBreakdown | null;
      missing_items: BedMissingItem[];
      amount_to_pay_cents: number | null;
      complete: boolean;
      bed_status: string | null;
    };
    return {
      status: 'ok',
      breakdown: result?.breakdown ?? null,
      missingItems: result?.missing_items ?? [],
      amountToPayCents: result?.amount_to_pay_cents ?? null,
      complete: Boolean(result?.complete),
      bedStatus: result?.bed_status ?? null,
    };
  } catch (err) {
    console.error('[BED-DRAFT] Exception finalize_bed_submission:', err);
    return { status: 'error' };
  }
}

/** Bucket privé dédié aux pièces jointes du BED (00177). */
export const BED_UPLOAD_BUCKET = 'bed-uploads';

export type BedUploadResult =
  | { status: 'ok'; certificate: BedCertificate }
  | { status: 'too_large' }
  | { status: 'bad_type' }
  | { status: 'error' };

const CERTIFICATE_MAX_BYTES = 8 * 1024 * 1024;
const CERTIFICATE_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];

/**
 * Dépose un certificat médical. JAMAIS bloquant (cahier L120) : un échec ne
 * doit pas empêcher la soumission, l'appelant se contente de l'afficher.
 */
export async function uploadBedCertificate(
  token: string,
  role: 'pilot' | 'copilot',
  file: File
): Promise<BedUploadResult> {
  if (file.size > CERTIFICATE_MAX_BYTES) return { status: 'too_large' };
  if (file.type && !CERTIFICATE_MIME.includes(file.type)) return { status: 'bad_type' };

  // Nom de fichier assaini : le chemin est confiné au dossier du token.
  const safeName = file.name.replace(/[^\w.-]+/g, '_').slice(-80);
  const path = `${token}/${role}-${Date.now()}-${safeName}`;

  try {
    const { error } = await supabase.storage
      .from(BED_UPLOAD_BUCKET)
      .upload(path, file, { upsert: true, contentType: file.type || undefined });

    if (error) {
      console.error('[BED-DRAFT] upload certificat:', error);
      return { status: 'error' };
    }

    return {
      status: 'ok',
      certificate: {
        path,
        name: file.name,
        size: file.size,
        uploaded_at: new Date().toISOString(),
      },
    };
  } catch (err) {
    console.error('[BED-DRAFT] Exception upload certificat:', err);
    return { status: 'error' };
  }
}
